import type { NameSpan } from './verses.js';

/**
 * A MARK ON A VERSE — §11's second half, and the part a screen share cannot do at all.
 *
 * A leader points at *these words here*, and every reader sees them marked **in their own edition**,
 * at their own size, in their own language. That is the whole argument for relaying a pointer rather
 * than pixels: the text is already on every device (D10), so what travels is where to look.
 *
 * WHY A MARK CANNOT BE A CHARACTER RANGE AND MEAN ANYTHING, which §11 states and this file obeys:
 * offsets address the edition they were measured in. A span marked in an English translation
 * addresses nothing in the Hebrew, and the reader beside you may be in either. So a mark carries the
 * **verse reference AND the edition its offsets belong to**, and degrades honestly — a reader on the
 * same edition sees the exact words, a reader on another sees the verse marked and nothing narrower.
 * Silently mapping offsets between translations would put the highlight on the wrong words, which is
 * worse than a coarser mark and is the one failure a screen share does not have.
 *
 * NOTHING HERE IS ABOUT A ROOM. A mark is a fact about a passage, so this file knows nothing about
 * who may make one, how one travels, or how long it lives — the same separation `chat.ts` keeps.
 */
export interface Mark {
  /** The verse, by the citation every client already addresses it with. */
  ref: string;
  /** The edition the offsets below were measured in — a rendering's NAME, as `PassageText` shows it. */
  edition: string;
  /** Character offsets into that edition's text of that verse: `[start, end)`. */
  start: number;
  end: number;
}

/**
 * One stretch of a verse as it should be drawn: plain, a name that leads somewhere, marked, or both.
 *
 * `link` is the node id a name leads to — the same thing `NameSpan.id` carries — so a renderer needs
 * nothing but this list to draw the whole verse.
 */
export interface TextRun {
  text: string;
  link?: string;
  marked: boolean;
}

/** A half-open range, clamped and sane, or null. */
function within(text: string, start: number, end: number): { start: number; end: number } | null {
  const from = Math.max(0, Math.min(Math.trunc(start), text.length));
  const to = Math.max(0, Math.min(Math.trunc(end), text.length));
  return to > from ? { start: from, end: to } : null;
}

/**
 * THE VERSE, CUT AT EVERY BOUNDARY THAT MATTERS — names, marks, and where they overlap.
 *
 * A mark and a name are not exclusive: a leader marks a phrase, and half of it is a name the reader
 * can travel to. Cutting at the union of both sets of edges is what lets a renderer draw each run
 * once and keep both meanings, instead of choosing which to lose.
 *
 * NEVER TRUSTS A RANGE, and that is not defensiveness for its own sake: `nameSpans` is computed by
 * the build, and marks arrive **over a channel anybody in a room can publish on**. An overlapping,
 * inverted, fractional or out-of-bounds range is dropped or clamped rather than allowed to produce
 * text that is not the verse. The one thing this function may never do is change what the verse says
 * — every run concatenated is the input, exactly.
 */
export function runsOf(
  text: string,
  links: readonly NameSpan[],
  marks: readonly { start: number; end: number }[] = [],
): TextRun[] {
  if (text === '') return [];

  const named = links
    .map(span => ({ ...within(text, span.start, span.end), id: span.id }))
    .filter((span): span is { start: number; end: number; id: string } => span.start !== undefined)
    .sort((a, b) => a.start - b.start);

  const marked = marks
    .map(span => within(text, span.start, span.end))
    .filter((span): span is { start: number; end: number } => span !== null)
    .sort((a, b) => a.start - b.start);

  /* Every edge, in order, with the ends of the text: the runs are what lies between them. */
  const edges = new Set<number>([0, text.length]);
  for (const span of [...named, ...marked]) { edges.add(span.start); edges.add(span.end); }
  const cuts = [...edges].sort((a, b) => a - b);

  const runs: TextRun[] = [];
  for (let i = 0; i + 1 < cuts.length; i += 1) {
    const start = cuts[i];
    const end = cuts[i + 1];
    if (end <= start) continue;
    /* A name that overlaps another is dropped rather than nested: `nameSpans` never overlaps, and a
       run that belonged to two names would have to choose one anyway. */
    const link = named.find(span => span.start <= start && span.end >= end);
    runs.push({
      text: text.slice(start, end),
      ...(link ? { link: link.id } : {}),
      marked: marked.some(span => span.start <= start && span.end >= end),
    });
  }
  return runs;
}
